import { useEffect, useReducer } from "react";
import { usePage } from "@inertiajs/react";

// Keep read markers across navigation even when browser storage is unavailable.
const readResponses = new Map();

function readResponse(key) {
  const remembered = readResponses.get(key) || "";
  try {
    const stored = globalThis.localStorage?.getItem(key) || "";
    return stored > remembered ? stored : remembered;
  } catch (_error) {
    return remembered;
  }
}

export default function useChatbotReadState({ chatId, messages, active = true }) {
  const { current_user: currentUser } = usePage().props;
  const storagePrefix = currentUser?.email ? `obsero.chatbot.read_response.${currentUser.email}.` : null;
  const [, refresh] = useReducer((value) => value + 1, 0);
  // Compare the server's ISO timestamps directly to preserve microseconds.
  const latestResponseAt = messages.reduce((latest, message) => (
    message.role === "assistant" && message.created_at > latest ? message.created_at : latest
  ), "");

  useEffect(() => {
    if (!storagePrefix) return undefined;

    const onStorage = (event) => {
      if (event.key?.startsWith(storagePrefix)) {
        if (event.newValue) readResponses.set(event.key, event.newValue);
        else readResponses.delete(event.key);
        refresh();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storagePrefix]);

  useEffect(() => {
    if (!storagePrefix || !chatId || !latestResponseAt || !active) return undefined;

    const markRead = () => {
      if (document.visibilityState !== "visible") return;

      const key = `${storagePrefix}${chatId}`;
      if ((readResponse(key) || "") >= latestResponseAt) return;

      readResponses.set(key, latestResponseAt);
      try {
        globalThis.localStorage?.setItem(key, latestResponseAt);
      } catch (_error) {
        // Read state still works for this session when storage is blocked.
      }
      refresh();
    };

    markRead();
    document.addEventListener("visibilitychange", markRead);
    return () => document.removeEventListener("visibilitychange", markRead);
  }, [storagePrefix, chatId, latestResponseAt, active]);

  return (chat) => Boolean(
    storagePrefix && chat.latest_response_at &&
    chat.latest_response_at > (readResponse(`${storagePrefix}${chat.id}`) || "")
  );
}
